import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Button,
  Box,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { Receivable, Util } from '@finley-technologies/finley-types';

import {
  useGetLoanProductsForCreditFacilityQuery,
  useGetReceivableResponseTapePreviewCsvDownloadUrlLazyQueryGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Select } from 'packages/common-frontend/components/Select';
import {
  errorToastId,
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { CreditFacilitySelect } from 'packages/credit-facility/ui/CreditFacilitySelect';

const getFilename = ({
  previewType,
  reportingDate,
  fileVersion,
  creditFacilitySlug,
  loanProductSlug,
  extension = 'csv',
}: {
  previewType: Receivable.Preview.ReceivableResponseTapePreviewAttachmentType;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  creditFacilitySlug: string;
  loanProductSlug: string;
  extension?: string;
}) => {
  return `${loanProductSlug}-${creditFacilitySlug}-${previewType}-${reportingDate}-v${fileVersion}.${extension}`;
};

export const DownloadPreviewButton = ({
  creditFacilities,
  fileVersion,
  reportingDate,
  previewIdentifier,
  organizationId,
}: {
  creditFacilities: { id: string; slug: string }[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  previewIdentifier: string;
  organizationId: string;
}) => {
  const toast = useToast();

  const { creditFacilityId } = useCreditFacilityContext();

  const [selectedLoanProductId, setSelectedLoanProductId] = useState<
    string | undefined
  >();

  const [
    selectedFileDownload,
    setSelectedFileDownload,
  ] = useState<Receivable.Preview.ReceivableResponseTapePreviewAttachmentType>(
    'extendedResponseTapePreview',
  );

  const [fileName, setFileName] = useState<string>();

  const [isFileDownloading, setIsFileDownloading] = useState<boolean>(false);

  const {
    data: loanProductsForCreditFacilityData,
    loading: loanProductsForCreditFacilityDataLoading,
  } = useGetLoanProductsForCreditFacilityQuery({
    onError: () => {
      errorToastConfig.title = `Unable to fetch loan products for credit facility.`;
      toast(errorToastConfig);
    },
    variables: { creditFacilityId, useWip: false },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.creditFacility.loanProducts.length > 0) {
        setSelectedLoanProductId(data.creditFacility.loanProducts[0].id);
      }
    },
  });

  useEffect(() => {
    if (loanProductsForCreditFacilityData) {
      const selectedLoanProduct = loanProductsForCreditFacilityData.creditFacility.loanProducts.find(
        (loanProduct) => loanProduct.id === selectedLoanProductId,
      );
      const selectedCreditFacility = creditFacilities.find(
        (creditFacility) => creditFacility.id === creditFacilityId,
      );
      if (selectedLoanProduct && selectedCreditFacility) {
        setFileName(
          getFilename({
            previewType: selectedFileDownload,
            reportingDate,
            fileVersion,
            loanProductSlug: selectedLoanProduct.slug,
            creditFacilitySlug: selectedCreditFacility.slug,
          }),
        );
      }
    }
  }, [
    creditFacilities,
    creditFacilityId,
    fileVersion,
    loanProductsForCreditFacilityData,
    reportingDate,
    selectedFileDownload,
    selectedLoanProductId,
  ]);

  const [
    getReceivableResponseTapePreviewCsvDownloadUrl,
  ] = useGetReceivableResponseTapePreviewCsvDownloadUrlLazyQueryGQLType({
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'Unable to download receivable response tape preview'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (
        response &&
        response.getReceivableResponseTapePreviewCsvDownloadUrl &&
        fileName
      ) {
        const {
          getReceivableResponseTapePreviewCsvDownloadUrl: url,
        } = response;
        axios({
          url,
          method: 'GET',
          responseType: 'blob',
        })
          .then((res) => {
            const fileBlob = new Blob([res.data], {
              type: res.data.type,
            });
            FileSaver.saveAs(fileBlob, fileName);
            successToastConfig.title = `Successfully downloaded ${fileName}`;
            toast(successToastConfig);
            setIsFileDownloading(false);
          })
          .catch(() => {
            if (!toast.isActive(errorToastId)) {
              errorToastConfig.title = `Error downloading ${fileName}`;
              toast(errorToastConfig);
              setIsFileDownloading(false);
            }
          });
      }
    },
  });

  const loanProductOptions =
    !loanProductsForCreditFacilityDataLoading &&
    loanProductsForCreditFacilityData
      ? loanProductsForCreditFacilityData.creditFacility.loanProducts.map(
          (loanProduct) => {
            return { label: loanProduct.name, value: loanProduct.id };
          },
        )
      : undefined;

  const fileDownloadOptions: {
    value: Receivable.Preview.ReceivableResponseTapePreviewAttachmentType;
    label: string;
  }[] = ([
    'extendedResponseTapePreview',
    'proposedExtendedResponseTapePreview',
  ] as Receivable.Preview.ReceivableResponseTapePreviewAttachmentType[]).map(
    (previewType) => {
      return {
        value: previewType,
        label: _.startCase(previewType),
      };
    },
  );

  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <Button
          borderRadius="8px"
          colorScheme="aurora"
          variant="outline"
          border="2px solid"
          rightIcon={<ChevronDownIcon />}
        >
          Download Previews
        </Button>
      </PopoverTrigger>
      <PopoverContent
        borderRadius="4px"
        padding="24px"
        background={colors.background[50]}
        width="auto"
      >
        <Text fontWeight="600">Download File</Text>
        <Box m="32px 0px" background="white">
          <CreditFacilitySelect
            displayInputFloatingLabel
            inputFloatingLabelBackgroundColor={colors.millet[50]}
            organizationId={organizationId}
            width="100%"
            creditFacilitySlugFilter={(creditFacilitySlug) => {
              return creditFacilities
                .map((creditFacility) => creditFacility.slug)
                .includes(creditFacilitySlug);
            }}
          />
        </Box>
        {loanProductOptions && selectedLoanProductId && (
          <Box mb="32px" background="white">
            <Select
              value={selectedLoanProductId}
              options={loanProductOptions}
              inputFloatingLabel="Loan Product"
              inputFloatingLabelBackgroundColor={colors.millet[50]}
              onChange={setSelectedLoanProductId}
            />
          </Box>
        )}
        <Box mb="32px" background="white">
          <Select
            value={selectedFileDownload}
            options={fileDownloadOptions}
            onChange={(previewType) => {
              if (previewType) {
                setSelectedFileDownload(previewType);
              }
            }}
            inputFloatingLabel="File"
            inputFloatingLabelBackgroundColor={colors.millet[50]}
          />
        </Box>

        <Flex direction="row-reverse">
          <Button
            borderRadius="8px"
            colorScheme="aurora"
            border="2px solid"
            isLoading={isFileDownloading}
            onClick={() => {
              if (selectedLoanProductId && fileName) {
                setIsFileDownloading(true);
                Sentry.addBreadcrumb({
                  category: 'ui.click',
                  message: `Clicked download for ${fileName} in assignment summary`,
                  level: Sentry.Severity.Info,
                });
                getReceivableResponseTapePreviewCsvDownloadUrl({
                  variables: {
                    creditFacilityId,
                    loanProductId: selectedLoanProductId,
                    reportingDate,
                    fileVersion,
                    type: selectedFileDownload,
                    previewIdentifier,
                  },
                });
              }
            }}
            isDisabled={
              loanProductsForCreditFacilityDataLoading || !selectedLoanProductId
            }
          >
            Download
          </Button>
        </Flex>
      </PopoverContent>
    </Popover>
  );
};
